import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UnaddressedAlertItem } from '@/hooks/usePortfolioRisk';

// ─── Types ──────────────────────────────────────────────────────────────────

interface UnaddressedAlertsWidgetProps {
  count: number;
  items: UnaddressedAlertItem[];
  loading?: boolean;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function WidgetSkeleton() {
  return (
    <div role="status" aria-label="Loading unaddressed alerts data" className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-muted" aria-hidden="true" />
      ))}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Unaddressed Alerts widget — FR-086 Widget 2.
 * Contracts where AI flagged alerts (APPROVED_WITH_ALERTS) but reviewer
 * has not yet annotated them.
 * Amber accent, links to filtered contracts list.
 */
export function UnaddressedAlertsWidget({ count, items, loading }: UnaddressedAlertsWidgetProps) {
  return (
    <section
      className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4 space-y-3"
      aria-label="Unaddressed alert items"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Unaddressed Alerts</h3>
        </div>
        <span
          className="rounded-full bg-amber-100 dark:bg-amber-900 px-2 py-0.5 text-xs font-bold text-amber-700 dark:text-amber-300"
          aria-live="polite"
          aria-label={`${count} contracts with unaddressed alerts`}
        >
          {count}
        </span>
      </div>

      {loading ? (
        <WidgetSkeleton />
      ) : items.length === 0 ? (
        <p className="text-xs text-amber-700 dark:text-amber-300 italic">No unaddressed alerts.</p>
      ) : (
        <ul className="space-y-1.5" role="list" aria-label="Contracts with unaddressed alerts">
          {items.slice(0, 5).map((item) => (
            <li key={item.contract_id} className="flex items-center justify-between gap-2">
              <Link
                to={`/contracts/${item.contract_id}`}
                className={cn(
                  'truncate text-xs text-amber-800 dark:text-amber-200 underline hover:no-underline',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
                )}
                title={item.title}
              >
                {item.title}
              </Link>
              <span className="shrink-0 text-xs text-amber-600 dark:text-amber-400 font-medium">
                {item.alert_count} alert{item.alert_count !== 1 ? 's' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Link
        to="/contracts?filter=unaddressed_alerts"
        className="block text-xs font-medium text-amber-700 dark:text-amber-300 underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        View all contracts with alerts
      </Link>
    </section>
  );
}
